(() => {
  'use strict';

  if (customElements.get('tuck-product-match')) return;

  class TuckProductMatch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('[data-tuck-product-match-track]');
      this.previous = this.querySelector('[data-tuck-product-match-previous]');
      this.next = this.querySelector('[data-tuck-product-match-next]');
      this.pagination = this.querySelector('[data-tuck-product-match-pagination]');
      this.status = this.querySelector('[data-tuck-product-match-status]');
      if (!this.track) return;

      this.boundClick = this.onClick.bind(this);
      this.boundChange = this.onChange.bind(this);
      this.boundScroll = this.updateControls.bind(this);
      this.addEventListener('click', this.boundClick);
      this.addEventListener('change', this.boundChange);
      this.track.addEventListener('scroll', this.boundScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.updateLayout());
      this.resizeObserver.observe(this);
      requestAnimationFrame(() => this.updateLayout());

      this.emitAnalytics('pdp_recommendation_viewed', {
        productId: Number(this.dataset.productId),
        recommendationCount: Number(this.dataset.recommendationCount || 0)
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundClick);
      this.removeEventListener('change', this.boundChange);
      if (this.track) this.track.removeEventListener('scroll', this.boundScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.initialized = false;
    }

    onClick(event) {
      const previous = event.target.closest('[data-tuck-product-match-previous]');
      const next = event.target.closest('[data-tuck-product-match-next]');
      if (previous || next) {
        const distance = this.pageDistance();
        this.track.scrollBy({
          left: previous ? -distance : distance,
          behavior: this.reducedMotion() ? 'auto' : 'smooth'
        });
        return;
      }

      const addButton = event.target.closest('[data-tuck-product-match-add]');
      if (addButton) {
        this.addProduct(addButton);
        return;
      }

      const productLink = event.target.closest('[data-tuck-product-match-link]');
      if (productLink) {
        const card = productLink.closest('[data-tuck-product-match-card]');
        this.emitAnalytics('pdp_recommendation_clicked', {
          productId: Number(card?.dataset.productId)
        });
      }
    }

    onChange(event) {
      if (!event.target.matches('[data-tuck-product-match-variant]')) return;
      const card = event.target.closest('[data-tuck-product-match-card]');
      const option = event.target.selectedOptions[0];
      const price = card?.querySelector('[data-tuck-product-match-price]');
      const compare = card?.querySelector('[data-tuck-product-match-compare]');
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
      if (compare) {
        compare.textContent = option?.dataset.compare || '';
        compare.hidden = option?.dataset.hasCompare !== 'true';
      }
      const button = card?.querySelector('[data-tuck-product-match-add]');
      if (button) button.disabled = !event.target.value;
    }

    async addProduct(button) {
      const card = button.closest('[data-tuck-product-match-card]');
      const variant = card?.querySelector('[data-tuck-product-match-variant]');
      const error = card?.querySelector('[data-tuck-product-match-error]');
      const variantId = Number(variant?.value);
      if (!variantId) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (error) error.hidden = true;

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add this item.');

        this.status.textContent = 'Added to cart.';
        this.emitAnalytics('pdp_recommendation_added', {
          productId: Number(card.dataset.productId),
          variantId
        });

        if (window.tuckCartController) {
          await window.tuckCartController.refreshSection('pdp-recommendation');
          window.tuckCartController.open(button);
        } else {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      } catch (requestError) {
        if (error) {
          error.textContent = requestError.message || 'Could not add this item.';
          error.hidden = false;
        }
      } finally {
        if (document.contains(button)) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    }

    updateLayout() {
      const firstImage = this.track.querySelector('.tuck-product-match-card__image');
      if (firstImage) {
        const carousel = this.querySelector('[data-tuck-product-match-carousel]');
        const imageRect = firstImage.getBoundingClientRect();
        const carouselRect = carousel.getBoundingClientRect();
        const center = imageRect.top - carouselRect.top + imageRect.height / 2;
        this.style.setProperty('--tuck-match-arrow-top', `${center}px`);
      }
      this.buildPagination();
      this.updateControls();
    }

    buildPagination() {
      if (!this.pagination) return;
      const card = this.track.querySelector('[data-tuck-product-match-card]');
      if (!card) return;
      const distance = this.pageDistance();
      const pages = Math.max(1, Math.ceil((this.track.scrollWidth - this.track.clientWidth) / distance) + 1);
      if (this.pagination.children.length === pages) return;
      this.pagination.replaceChildren(...Array.from({ length: pages }, () => document.createElement('span')));
      this.updateControls();
    }

    pageDistance() {
      const cards = [...this.track.querySelectorAll('[data-tuck-product-match-card]')];
      if (!cards.length) return this.track.clientWidth;

      const styles = getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || 0);
      const padding = Number.parseFloat(styles.paddingLeft || 0) + Number.parseFloat(styles.paddingRight || 0);
      const step = cards[0].getBoundingClientRect().width + gap;
      const visibleCards = Math.max(1, Math.floor((this.track.clientWidth - padding + gap + 1) / step));
      return step * visibleCards;
    }

    updateControls() {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (this.previous) this.previous.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= maxScroll - 2;

      const card = this.track.querySelector('[data-tuck-product-match-card]');
      if (!card || !this.pagination) return;
      const activePage = Math.round(this.track.scrollLeft / Math.max(1, this.pageDistance()));
      [...this.pagination.children].forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activePage);
      });
    }

    emitAnalytics(event, detail = {}) {
      document.dispatchEvent(new CustomEvent(`tuckchar:${event}`, { detail }));
      document.dispatchEvent(new CustomEvent('tuckchar:analytics', {
        detail: { event, ...detail }
      }));
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('tuck-product-match', TuckProductMatch);
})();
